import sortBy from 'lodash/sortBy'
import { number as getClosest } from 'get-closest'

import { store } from 'app'
import {
  TOGGLE_FILTER,
  TOGGLE_FILTER_EXPANSION,
  CLEAR_ALL_FILTERS,
  SET_ORDER_BY
} from 'environments/TasteProfileOnboarding/actions'
import {
  TASTE_PROFILE_FILTER,
  PRICE_FILTER,
  ORDER_BY_FILTER,
  COUNTRY_FILTER,
  FILTER_DISPLAY_TYPES
} from 'constants/filtering'
import urlify from 'utils/urlify'

const getPriceFromParam = (query, param) => {
  const value = query[param]
  if (value) {
    const price = parseFloat(value)
    const index = getClosest(price, PRICE_FILTER.OPTIONS)
    return PRICE_FILTER.OPTIONS[index]
  }
  return undefined
}

export const getInitialState = (tasteProfiles, selectedWineIndex = 0, expandedFilterPanels) => {
  // retrieve initial order by / filter options via GET params
  let orderByOptions = ORDER_BY_FILTER.OPTIONS
  let minPrice
  let maxPrice
  let countryFilter

  const state = store.getState()
  if (state.routing && state.routing.locationBeforeTransitions) {
    const { query } = state.routing.locationBeforeTransitions
    const orderByParam = query[ORDER_BY_FILTER.GET_PARAM]
    if (orderByParam) {
      orderByOptions = ORDER_BY_FILTER.OPTIONS.map((option) => {
        option.selected = (urlify(option.label) === orderByParam)
        return option
      })
    }

    const wineColorParam = query[TASTE_PROFILE_FILTER.GET_PARAM]
    if (wineColorParam) {
      const color = TASTE_PROFILE_FILTER.OPTIONS.indexOf(wineColorParam)
      if (color !== -1) {
        selectedWineIndex = color // eslint-disable-line no-param-reassign
      }
    }

    minPrice = getPriceFromParam(query, PRICE_FILTER.GET_PARAM.min)
    maxPrice = getPriceFromParam(query, PRICE_FILTER.GET_PARAM.max)

    countryFilter = (query[COUNTRY_FILTER.GET_PARAM] || '')
      .split(COUNTRY_FILTER.JOIN_MULTIPLE_ON)
      .filter((text) => !!text)
  }

  return {
    orderings: {
      name: ORDER_BY_FILTER.NAME,
      type: FILTER_DISPLAY_TYPES.select,
      options: orderByOptions,
      expanded: true
    },
    filters: [{
      name: TASTE_PROFILE_FILTER.NAME,
      type: FILTER_DISPLAY_TYPES.radio,
      options: tasteProfiles.map((tasteProfile, index) => ({
        label: tasteProfile.name,
        value: tasteProfile.name,
        // FIXME in GG-9988, obsolete if not used in RadioFilter.js#29
        color: tasteProfile.color,
        selected: index === selectedWineIndex
      })),
      expanded: expandedFilterPanels
        ? expandedFilterPanels.includes(TASTE_PROFILE_FILTER.NAME)
        : true
    }, {
      name: PRICE_FILTER.NAME,
      type: FILTER_DISPLAY_TYPES.range,
      steps: PRICE_FILTER.OPTIONS,
      min: minPrice || PRICE_FILTER.OPTIONS[0],
      max: maxPrice || PRICE_FILTER.OPTIONS.slice(-1)[0],
      expanded: expandedFilterPanels ? expandedFilterPanels.includes(PRICE_FILTER.NAME) : true
    }, {
      name: COUNTRY_FILTER.NAME,
      type: FILTER_DISPLAY_TYPES.checkbox,
      options: [...COUNTRY_FILTER.OPTIONS
        .map((option) => {
          if (countryFilter && countryFilter.indexOf(option.value) !== -1) {
            return { ...option, selected: true }
          }
          return option
        })],

      expanded: (expandedFilterPanels && expandedFilterPanels.includes(COUNTRY_FILTER.NAME)) ||
      countryFilter.length !== 0
    }]
  }
}

export const orderingsReducer = (tasteProfileState, action) => {
  const { orderings } = tasteProfileState
  switch (action.type) {
    case SET_ORDER_BY: {
      const { value } = action.payload
      const selected = orderings.options.find((option) => option.selected)
      if (selected && selected.value === value) {
        // Nothing changes
        return orderings
      }

      return Object.assign({}, orderings, {
        options: orderings.options.map((option) => ({
          ...option,
          selected: option.value === value
        }))
      })
    }
    default:
      return orderings
  }
}

export const filtersReducer = (tasteProfileState, action) => {
  const { filters } = tasteProfileState
  switch (action.type) {
    case CLEAR_ALL_FILTERS: {
      // Reset all filters to initial state but keep the selected wine
      const tasteProfiles = sortBy(tasteProfileState.result.tasteProfiles, (tasteProfile) => tasteProfile.color)
      const selectedWineIndex = tasteProfileState.filters
        .find(({ name }) => name === TASTE_PROFILE_FILTER.NAME)
        .options
        .findIndex(({ selected }) => selected)
      const expandedFilterPanels = tasteProfileState.filters
        .filter((filter) => filter.expanded)
        .map((filter) => filter.name)
      return getInitialState(tasteProfiles, selectedWineIndex, expandedFilterPanels).filters
    }
    case TOGGLE_FILTER_EXPANSION: {
      return filters.map((filter) => {
        if (filter.name === action.payload.name) {
          return { ...filter, expanded: !action.payload.expanded }
        }
        return filter
      })
    }
    case TOGGLE_FILTER: {
      const { name, value, allowMultiple } = action.payload
      const index = filters.findIndex((f) => f.name === name)

      switch (name) {
        case COUNTRY_FILTER.NAME:
        case TASTE_PROFILE_FILTER.NAME: {
          const options = filters[index].options
          const isSelected = options.find((o) => o.selected)
          if (!allowMultiple && isSelected && isSelected.value === value) {
            // Nothing changes
            return filters
          }
          return filters.map((filter, i) => {
            // For the current filter, toggle selected value
            if (i === index) {
              filter.options = options.map(
                (option) => {
                  let selected = option.selected
                  if (allowMultiple) {
                    if (option.value === value) {
                      selected = !selected
                    }
                  } else {
                    selected = option.value === value
                  }
                  return {
                    ...option,
                    selected
                  }
                }
              )
            }
            return filter
          })
        }
        case PRICE_FILTER.NAME: {
          const [min, max] = value
          return filters.map((filter, i) => {
            if (i === index) {
              return {
                ...filter,
                min,
                max
              }
            }
            return filter
          })
        }
        default:
          return filters
      }
    }

    default:
      return filters
  }
}
